import { useEffect, useState } from "react";
import { isNative } from "../lib/platform";

// ¿El teclado en pantalla está abierto? (dictado del fundador 2026-08-18:
// "se roba demasiada pantalla el anota lo que hiciste" — con el teclado
// arriba, el chrome de la app tiene que ceder la pantalla al contenido).
//
// NATIVO (Android/iOS): los eventos del plugin @capacitor/keyboard, la verdad
// del sistema.
//
// WEB móvil: dos señales JUNTAS, ganadas en revisión adversarial 2026-08-18:
//  (1) hay FOCO en un campo editable (sin foco no hay teclado), y
//  (2) el alto EFECTIVO del viewport se achicó más que el umbral respecto del
//      alto BASE medido sin foco.
// El alto efectivo multiplica por `visualViewport.scale`: el pinch-zoom
// achica vv.height pero no el alto efectivo, así que hacer zoom JAMÁS esconde
// la barra (falso positivo confirmado por el auditor: castigaba justo a quien
// usa zoom por baja visión). Y el alto base se re-mide en cada evento sin
// foco, así que rotar el teléfono o el vaivén de las barras del navegador se
// re-calibran solos. La resta contra el alto base (y no innerHeight-vv.height)
// cubre los DOS modos reales del teclado web: `resizes-visual` (iOS: solo el
// visualViewport se achica) y `resizes-content` (nuestro index.html declara
// `interactive-widget=resizes-content`, así que en Chrome Android el layout
// entero se achica y la resta simple daba CERO — la feature nacía muerta en
// la plataforma del screenshot del fundador).
//
// En escritorio el viewport no se achica al escribir: foco sí, achique no,
// y el hook queda en false. Sin visualViewport se usa innerHeight (el modo
// resizes-content igual se detecta); si nada se achica, no se adivina.
const UMBRAL_TECLADO = 150;

// Kill-switch ON por defecto (regla del repo): esto toca el chrome de TODA la
// app con una heurística de navegador — ante una sorpresa en un dispositivo
// raro, apagarlo devuelve la barra de siempre sin redeploy de lógica.
const TECLADO_CEDE = process.env.REACT_APP_TECLADO_CEDE_ENABLED !== "false";

const esEditable = (el) => !!el && (el.tagName === "TEXTAREA" || el.tagName === "INPUT" || el.isContentEditable);

function altoEfectivo() {
  const vv = window.visualViewport;
  return vv ? vv.height * (vv.scale || 1) : window.innerHeight;
}

export default function useTecladoAbierto() {
  const [abierto, setAbierto] = useState(false);
  useEffect(() => {
    if (!TECLADO_CEDE) return undefined;
    if (isNative) {
      // Import dinámico: el plugin solo existe (y solo importa) en la app
      // instalada; la web no paga el bundle. `vivos` se re-chequea tras CADA
      // await (revisión adversarial: el desmontaje puede pillar al bridge a
      // mitad de camino y sin esto el listener nativo quedaba huérfano para
      // siempre). Si algo falla, quedamos en false: la barra de siempre.
      let vivos = true;
      const subs = [];
      (async () => {
        try {
          const { Keyboard } = await import("@capacitor/keyboard");
          if (!vivos) return;
          const s1 = await Keyboard.addListener("keyboardWillShow", () => setAbierto(true));
          if (!vivos) { s1.remove(); return; }
          subs.push(s1);
          const s2 = await Keyboard.addListener("keyboardWillHide", () => setAbierto(false));
          if (!vivos) { s2.remove(); return; }
          subs.push(s2);
        } catch { /* plugin ausente: comportamiento de siempre */ }
      })();
      return () => {
        vivos = false;
        subs.forEach((s) => s?.remove?.());
      };
    }
    // WEB: foco editable + achique contra el alto base sin foco.
    let altoBase = altoEfectivo();
    let foco = esEditable(document.activeElement);
    let focoTimer = null;
    const medir = () => {
      const alto = altoEfectivo();
      if (!foco) {
        // Sin foco no hay teclado: este alto ES la base. Si el focusout dejó
        // una base achicada (el teclado aún cerrándose), el resize del cierre
        // pasa por aquí y la sana solo.
        altoBase = alto;
        setAbierto(false);
        return;
      }
      setAbierto(altoBase - alto > UMBRAL_TECLADO);
    };
    const onFocusIn = (e) => {
      if (!esEditable(e.target)) return;
      foco = true;
      medir();
    };
    const onFocusOut = () => {
      // El foco puede estar saltando de un campo a otro: se resuelve al
      // próximo tick, cuando document.activeElement ya es el destino real.
      clearTimeout(focoTimer);
      focoTimer = setTimeout(() => {
        foco = esEditable(document.activeElement);
        medir();
      }, 0);
    };
    const vv = window.visualViewport;
    vv?.addEventListener("resize", medir);
    window.addEventListener("resize", medir);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    medir();
    return () => {
      clearTimeout(focoTimer);
      vv?.removeEventListener("resize", medir);
      window.removeEventListener("resize", medir);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
  return abierto;
}
